import { Link } from "react-router-dom";
import { LandingHeader } from "../components/landing-chrome";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-ink-50">
      <LandingHeader />
      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <article className="prose prose-ink max-w-none">
          <h1 className="text-3xl font-bold tracking-tight text-ink-900">
            Privacy Policy
          </h1>
          <p className="mt-2 text-sm text-ink-500">
            Last updated: June 9, 2026
          </p>

          <p className="mt-4 text-ink-700">
            This Privacy Policy explains what BrandEcho ("we", "us") collects,
            why, and your choices. We don't sell your data and we don't use
            your private content to train external models.
          </p>

          <Section title="1. What we collect">
            <ul className="ml-6 list-disc space-y-1">
              <li><b>Account info</b>: email, name, password hash (bcrypt) when you sign up with email; X user id, X handle, display name, and avatar when you sign in with X.</li>
              <li><b>Content you provide</b>: long-form text inputs, generated post variants, voice training samples, scheduled posts, drafts, brand logos.</li>
              <li><b>Connected social accounts</b>: encrypted OAuth credentials for the platforms you connect (X, Bluesky, LinkedIn, Threads, Instagram).</li>
              <li><b>Usage</b>: AI calls, generation counts, plan and credit balance.</li>
              <li><b>Billing</b>: plan tier and Stripe customer id. Card numbers never touch our servers.</li>
              <li><b>Technical</b>: standard server logs (IP address, user agent, request paths) for security and debugging.</li>
            </ul>
          </Section>

          <Section title="2. Why we collect it">
            <ul className="ml-6 list-disc space-y-1">
              <li>To provide the Service (generate, publish, schedule, analytics).</li>
              <li>To authenticate you and protect your account.</li>
              <li>To bill you correctly and send receipts.</li>
              <li>To debug failures and detect abuse.</li>
            </ul>
          </Section>

          <Section title="3. How we store it">
            All data lives in Cloudflare D1 (SQL), KV (sessions, secrets), and
            R2 (uploads). Social-account tokens are encrypted at rest. Servers
            are in Cloudflare's global network. We retain data while your
            account is active and delete it within 30 days of account deletion.
          </Section>

          <Section title="4. Sub-processors">
            <ul className="ml-6 list-disc space-y-1">
              <li><b>Cloudflare</b> — hosting, edge compute, storage.</li>
              <li><b>OpenAI / Anthropic</b> — AI model inference (we send your prompts; we never include your contact info).</li>
              <li><b>Stripe</b> — billing.</li>
              <li><b>Resend</b> — transactional email.</li>
            </ul>
            We do not sell your personal information to anyone.
          </Section>

          <Section title="5. AI training">
            We do not train machine-learning models on your private content.
            Our AI provider sub-processors operate under their own no-train
            agreements for API traffic.
          </Section>

          <Section title="6. Cookies">
            We set one essential first-party cookie (<code>bsid</code>) to keep
            you signed in. We do not use third-party advertising cookies.
          </Section>

          <Section title="7. Your choices">
            <ul className="ml-6 list-disc space-y-1">
              <li>Update or delete content from inside the app.</li>
              <li>Disconnect any social account in Settings.</li>
              <li>Cancel your subscription in Settings → Billing.</li>
              <li>Delete your entire account in Settings → Danger zone (this purges all data within 30 days).</li>
              <li>Export or request a copy of your data — email us.</li>
            </ul>
          </Section>

          <Section title="8. Children">
            BrandEcho is not for users under 13. We do not knowingly collect
            information from children.
          </Section>

          <Section title="9. International transfers">
            Cloudflare may serve our content from any of its global edge
            locations. Your data is processed wherever Cloudflare or our
            sub-processors operate.
          </Section>

          <Section title="10. Changes to this policy">
            We'll post the updated version here and email account holders for
            material changes.
          </Section>

          <Section title="11. Contact">
            Questions or data requests:{" "}
            <a href="mailto:privacy@brandecho.com">privacy@brandecho.com</a>
          </Section>

          <p className="mt-10 text-sm">
            <Link to="/" className="text-brand-600 hover:underline">
              ← Back to BrandEcho
            </Link>
          </p>
        </article>
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <>
      <h2 className="mt-8 text-xl font-semibold text-ink-900">{title}</h2>
      <div className="mt-2 text-ink-700">{children}</div>
    </>
  );
}
